import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Container, Table, Button, Alert, OverlayTrigger, Tooltip, Spinner } from "react-bootstrap";
import WeekSelector from "../weekSelector";
import { useTranslation } from 'react-i18next';
import { SiUber } from "react-icons/si";
import { FaCar, FaBolt } from "react-icons/fa";

const visibleColumns = {
  uber: ["driver_firstName", "driver_lastName", "incBrutto", "incNetto", "incCash", "appFee", "appFeeTaxes", "bonus", "tips"],
  bolt: ["driver_firstName", "driver_lastName", "incBrutto", "incBruttoApp", "incBruttoCash", "incNettoCash", "appFee", "incNetto", "totalInc", "bonus", "tips"],
  freenow: ["driver_firstName", "driver_lastName", "brutto", "saldo_freenow", "cash", "bonus", "tips"]

};

const SERVICE_OPTIONS = [
  { id: "uber", label: "Uber", icon: SiUber, color: "#000000", path: "/admin/edit-source-data/uber" },
  { id: "bolt", label: "Bolt", icon: FaBolt, color: "#00D66C", path: "/admin/edit-source-data/bolt" },
  { id: "freenow", label: "FreeNow", icon: FaCar, color: "#E30613", path: "/admin/edit-source-data/freenow" },
];

const EditableIncoming = () => {
  const { service: serviceParam } = useParams();
  const navigate = useNavigate();
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [editingCell, setEditingCell] = useState({ row: null, key: null });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [invalidUsers, setInvalidUsers] = useState([]);
  const { t } = useTranslation();

  const service = serviceParam && SERVICE_OPTIONS.some((s) => s.id === serviceParam) ? serviceParam : null;

  useEffect(() => {
    if (!serviceParam) {
      navigate("/admin/edit-source-data/bolt", { replace: true });
      return;
    }
    if (!service) {
      navigate("/admin/edit-source-data/bolt", { replace: true });
    }
  }, [serviceParam, service, navigate]);

  const columnLabels = {
    driver_firstName: t("name"),
    driver_lastName: t("surname"),
    incBrutto: t("brutto"),
    incNetto: t("netto"),
    incCash: t("cash"),
    appFee: t("appfee"),
    appFeeTaxes: `${t("appfee")} ${t("tax")}`,
    bonus: t("bonus"),
    tips: t("tips"),
    incBruttoApp: t("brutto"),
    incBruttoCash: `${t("brutto")} ${t("cash")}`,
    incNettoCash: `${t("netto")} ${t("cash")}`,
    totalInc: t("total"),
    brutto: t("brutto"),
    cash: t("cash"),
    saldo_freenow: "Saldo FreeNow"
  };

  const fetchData = async () => {
    if (!service || !selectedWeek) return;
    setLoading(true);
    try {
      const res = await axios.get(`/api/get-${service}-data`, {
        params: { week_number: selectedWeek },
      });
      const data = res.data;

      const invalidChecks = await Promise.all(
        data.map(async (row) => {
          const { driver_firstName, driver_lastName } = row;
          try {
            const res = await axios.get("/api/user-exists", {
              params: { firstName: driver_firstName, lastName: driver_lastName },
            });
            return res.data.exists ? null : `${driver_firstName} ${driver_lastName}`;
          } catch (e) {
            return null;
          }
        })
      );

      const invalidList = invalidChecks.filter(Boolean);
      setInvalidUsers(invalidList);
      setTableData(data);
      setMessage(null);
    } catch (error) {
      console.error("❌ Помилка завантаження:", error);
      setMessage("Не вдалося завантажити дані з сервера.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (rowIndex, key, value) => {
    const updatedData = [...tableData];
    updatedData[rowIndex][key] = value.replace(',', '.');
    setTableData(updatedData);
  };


  const token = localStorage.getItem("token");

  const deleteRecord = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await axios.delete(
        `/api/${service}/record/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setTableData((prev) => prev.filter((row) => row.id !== id));
    } catch (error) {
      console.error("❌ Помилка при видаленні:", error);
      alert(t("deleteError"));
    }
  };

  const saveChanges = async () => {
    try {
      for (const row of tableData) {
        if (!row.id) continue;

        const cleanRecord = Object.fromEntries(
          Object.entries(row).filter(
            ([k, v]) => v !== null && v !== undefined && v !== ""
          )
        );

        // Фіксація created_at
        if (cleanRecord.created_at) {
          try {
            const date = new Date(cleanRecord.created_at);
            cleanRecord.created_at = date.toISOString().slice(0, 19).replace('T', ' ');
          } catch (e) {
            console.warn("⚠️ Неможливо конвертувати дату:", cleanRecord.created_at);
          }
        }

        // Дозволені поля для кожного сервісу
        const allowedFields = {
          uber: ["driver_firstName", "driver_lastName", "week_number", "cash", "incBrutto", "incNetto", "appFee", "appFeeTaxes", "bonus", "tips"],
          bolt: ["driver_firstName", "driver_lastName", "week_number", "incBrutto", "incBruttoApp", "incBruttoCash", "incNettoCash", "appFee", "incNetto", "totalInc", "bonus", "tips"],
          freenow: ["driver_firstName", "driver_lastName", "week_number", "cash", "brutto", "saldo_freenow", "bonus", "tips"]
        };

        if (allowedFields[service]) {
          Object.keys(cleanRecord).forEach(key => {
            if (!allowedFields[service].includes(key)) delete cleanRecord[key];
          });
        }


        const endpoint = `/api/${service}/record/${row.id}`;

        if (service === "freenow") {
          await axios.put(endpoint, [cleanRecord]);
        } else {
          await axios.put(endpoint, cleanRecord);
        }
      }

      alert("✅ Зміни збережено");
    } catch (error) {
      console.error("❌ Помилка збереження:", error);
      alert("❌ Помилка при збереженні змін");
    }
  };


  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getComparableValue = (value) => {
    const num = parseFloat(value);
    return !isNaN(num) && isFinite(num) ? num : String(value).toLowerCase();
  };

  const sortedData = [...tableData].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aVal = getComparableValue(a[sortConfig.key]);
    const bVal = getComparableValue(b[sortConfig.key]);

    if (typeof aVal === "number" && typeof bVal === "number") {
      return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
    }
    return sortConfig.direction === "asc"
      ? aVal.localeCompare(bVal)
      : bVal.localeCompare(aVal);
  });

  useEffect(() => {
    fetchData();
  }, [selectedWeek, service]);

  const currentColumns = visibleColumns[service] || [];

  return (
    <Container className="mt-4">
      <h2 className="text-center mb-4">{t("edit")} {t("records")}{service ? `: ${service}` : ""}</h2>

      <div className="d-flex flex-column flex-md-row align-items-stretch mb-3 gap-2 gap-md-3">
        <div className="d-flex flex-grow-1 flex-md-grow-1 w-100" style={{ minWidth: 0, flexBasis: 0 }}>
          <WeekSelector selectedWeek={selectedWeek} setSelectedWeek={setSelectedWeek} availableWeeksOnly className="h-100 mb-0 w-100" />
        </div>
        {SERVICE_OPTIONS.map(({ id, label, icon: Icon, color, path }) => (
          <div
            key={id}
            className="rounded d-flex align-items-center justify-content-center gap-2 text-decoration-none py-2 py-md-0 flex-grow-1 flex-md-grow-1 w-100"
            style={{
              minWidth: 0,
              flexBasis: 0,
              cursor: "pointer",
              border: `2px solid ${color}`,
              color: "#212529",
              minHeight: "38px",
            }}
            onClick={() => navigate(path)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && navigate(path)}
          >
            <Icon size={22} style={{ color }} className="flex-shrink-0" />
            <span className="fw-semibold">{label}</span>
          </div>
        ))}
      </div>

      {loading ? (
        <div
          className="mt-3 border border-primary rounded d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: "#f8f9fa",
            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
            minHeight: "200px"
          }}
        >
          <div className="d-flex flex-column align-items-center gap-2">
            <Spinner animation="border" variant="primary" style={{ width: "3rem", height: "3rem" }} />
            <span className="text-muted">{t("loading")}...</span>
          </div>
        </div>
      ) : message ? (
        <Alert variant="danger">{message}</Alert>
      ) : sortedData.length === 0 ? (
        <Alert variant="warning">{t("noRecords")}</Alert>
      ) : (
        <div
          className="mt-3 border border-primary rounded"
          style={{
            backgroundColor: "#f8f9fa",
            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)"
          }}
        >
          <Table bordered hover className="table table-bordered mt-4">
            <thead className="table-white text-center sticky-top bg-dark" style={{ zIndex: 10 }}>
              <tr>
                {currentColumns.map((key) => (
                  <th key={key} onClick={() => handleSort(key)} style={{ cursor: "pointer" }}>
                    {columnLabels[key] || key} {sortConfig.key === key && (sortConfig.direction === "asc" ? "▲" : "▼")}
                  </th>
                ))}
                <th>{t("delete")}</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((row, rowIndex) => {
                const fullName = `${row.driver_firstName} ${row.driver_lastName}`;
                const isInvalid = invalidUsers.includes(fullName);

                return (
                  <tr key={rowIndex}>
                    {currentColumns.map((key) => {
                      const value = row[key] ?? "";
                      const isNameField = key === "driver_firstName" || key === "driver_lastName";

                      return (
                        <td
                          key={key}
                          onDoubleClick={() => setEditingCell({ row: rowIndex, key })}
                          style={{ cursor: "pointer", position: "relative" }}
                        >
                          {editingCell.row === rowIndex && editingCell.key === key ? (
                            <input
                              autoFocus
                              type="text"
                              className="form-control"
                              value={value}
                              onChange={(e) => handleInputChange(rowIndex, key, e.target.value)}
                              onBlur={() => setEditingCell({ row: null, key: null })}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") setEditingCell({ row: null, key: null });
                              }}
                            />
                          ) : (
                            <>
                              {value}
                              {isNameField && isInvalid && (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={<Tooltip>{t("noUser")}</Tooltip>}
                                >
                                  <span className="text-danger ms-1">⚠️</span>
                                </OverlayTrigger>
                              )}
                            </>
                          )}
                        </td>
                      );
                    })}
                    <td className="text-center">
                      <Button variant="danger" size="sm" onClick={() => deleteRecord(row.id)}>🗑️</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      <div className="d-flex gap-2 mt-3" style={{ gap: "8px" }}>
        <Button
          variant="success"
          className="flex-grow-1"
          style={{ flex: "0 0 50%" }}
          onClick={saveChanges}
          disabled={loading || tableData.length === 0}
        >
          💾 {t("save")}
        </Button>
        <Button
          variant="danger"
          className="flex-grow-1"
          style={{ flex: "0 0 50%" }}
          disabled={tableData.length === 0}
          onClick={async () => {
            if (window.confirm(t("confirmdelete") + " " + tableData.length + " " + t("records") + "?")) {
              try {
                await axios.delete(`/api/${service}/all-records`, {
                  params: { week_number: selectedWeek },
                  headers: { Authorization: `Bearer ${token}` }
                });
                setTableData([]);
                alert(t("deleted"));
              } catch (err) {
                console.error("❌ Помилка при масовому видаленні:", err);
                alert(t("deleteError"));
              }
            }
          }}
        >
          🗑️ {t("delete")} {t("all")} ({tableData.length})
        </Button>
      </div>
    </Container>
  );
};

export default EditableIncoming;
